import { Box, ListItemButton, Stack, Typography } from '@mui/material';

import type { SxProps, Theme } from '@mui/material/styles';
import type { AccessCentreRole } from 'domains/accessCentre/types';

const roleListItemButtonSx = {
    alignItems: 'flex-start',
    py: 1.25,
    px: 1.5,
    borderRadius: 1,
};

const roleListItemContentStackSx: SxProps<Theme> = {
    minWidth: 0,
    typography: 'body1',
    fontFamily: 'inherit',
};

const roleListItemNameRowSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 1,
    width: '100%',
    minWidth: 0,
};

/** Up to two lines in the role picker; avoids horizontal scroll in narrow popovers. */
const roleNameInListTypographySx: SxProps<Theme> = {
    minWidth: 0,
    flex: 1,
    textAlign: 'left',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
};

export type RoleListItemContentProps = {
    role: AccessCentreRole;
    /** When true, shows user count on the first row (e.g. bulk change role dialog only). */
    showUserCount?: boolean;
    /** Extra styles merged onto the outer Stack (e.g. full width in Autocomplete). */
    stackSx?: SxProps<Theme>;
};

export const RoleListItemContent = ({ role, showUserCount = false, stackSx }: RoleListItemContentProps) => (
    <Stack
        spacing={0.5}
        sx={{
            ...roleListItemContentStackSx,
            ...(showUserCount && { alignItems: 'stretch' }),
            ...stackSx,
        }}
    >
        {showUserCount ? (
            <Box sx={roleListItemNameRowSx}>
                <Typography color="text.primary" sx={roleNameInListTypographySx}>
                    {role.name}
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ flexShrink: 0, textAlign: 'right' }}
                    data-testid={`access-center-bulk-role-option-user-count-${role.id}`}
                >
                    {role.user_count} {role.user_count === 1 ? 'user' : 'users'}
                </Typography>
            </Box>
        ) : (
            <Typography color="text.primary" sx={roleNameInListTypographySx}>
                {role.name}
            </Typography>
        )}
        <Typography
            variant={showUserCount ? 'body2' : 'helperText'}
            color="text.secondary"
            sx={showUserCount ? { textAlign: 'left', width: '100%' } : undefined}
        >
            {role.description ?? ''}
        </Typography>
    </Stack>
);

export type RoleListItemCellProps = {
    role: AccessCentreRole;
    isSelected: boolean;
    selectDisabled: boolean;
    onSelect: (role: AccessCentreRole) => void;
};

const RoleListItemCell = ({ role, isSelected, selectDisabled, onSelect }: RoleListItemCellProps) => (
    <ListItemButton
        role="option"
        selected={isSelected}
        onClick={() => onSelect(role)}
        disabled={selectDisabled}
        data-testid="access-center-user-role-option"
        sx={roleListItemButtonSx}
    >
        <RoleListItemContent role={role} />
    </ListItemButton>
);

export default RoleListItemCell;
